/**
 * @file NewsletterWidget/component.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 */

import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NEWSLETTER_GENERIC, OPEN_IN_NEW } from "./constants";
import LinkWrap from "../LinkWrap/component";
import { post_subscribe_newsletters_email } from "../../actions/Newsletters/actions";
import { Heading } from "../Heading/component";
import NewsletterForm from "./components/NewsletterForm/component";
import { getLogoImage } from "./utils";

export const NewsletterWidget = ({ promotenewsletter }) => {
    const newsletters = useSelector((state) => state.newsletters.newsletters);
    const subscribe = useSelector((state) => state.newsletters.subscribe);
    const previousValuesRef = useRef({
        promotenewsletter,
        name: NEWSLETTER_GENERIC.name,
    });
    const dispatch = useDispatch();
    const postSubscribeNewslettersEmail = (...args) =>
        dispatch(post_subscribe_newsletters_email(...args));

    const getSuccessMessage = (subscribe_info, config) => {
        const success_text =
            subscribe_info?.message?.success_message || config?.success_message;
        const success_url = `https://subscriptions.cbc.ca/listmanagement?cmp=feed_${config?.slug}_newsletter-subscribe`;
        const success_link = subscribe_info?.message?.result?.confirmation ? (
            ""
        ) : (
            <span>
                <br />
                Discover all CBC newsletters in the&nbsp;
                <LinkWrap
                    className="newsletterSubscriptionLink"
                    url={success_url}
                    external={true}
                    target="_blank"
                >
                    Subscription Centre.
                    <img
                        src={OPEN_IN_NEW}
                        className="opensNewWindow-icon"
                        alt="opens new window"
                    />
                </LinkWrap>
            </span>
        );
        const success_message = (
            <span className="newsletterWidgetSuccess" aria-live="polite">
                {success_text}
                <br />
                {success_link}
            </span>
        );
        return success_message;
    };

    const getSubInfo = () => {
        for (let i = 0; i < subscribe.length; i++) {
            if (subscribe[i].error) {
                if (subscribe[i].error?.newsletter_id === promotenewsletter) {
                    return subscribe[i];
                }
            } else {
                if (subscribe[i].message?.newsletter_id === promotenewsletter) {
                    return subscribe[i];
                }
            }
        }
    };

    const getInitialState = () => {
        if (newsletters && promotenewsletter) {
            const newsletter = promotenewsletter;
            const config = newsletters.find(
                (n) => n["newsletter_id"] === newsletter
            );
            const subscribe_info_exists =
                subscribe && subscribe[0] !== undefined;

            let subscribe_info = {};

            if (subscribe_info_exists) {
                subscribe_info = getSubInfo();
            }

            const sub_success = subscribe_info && subscribe_info.message;
            const success_message = getSuccessMessage(subscribe_info, config);

            const logo_image = getLogoImage(config);

            const button_text =
                config?.button_text.trim() !== ""
                    ? config?.button_text
                    : "Subscribe";

            return {
                newsletter,
                logo: logo_image,
                slogan: NEWSLETTER_GENERIC.slogan,
                desc: config?.description_widget || NEWSLETTER_GENERIC.desc,
                name: config?.name || NEWSLETTER_GENERIC.name,
                is_subscribed: sub_success || false,
                tried_subscribe: false,
                success_message:
                    success_message || NEWSLETTER_GENERIC.success_message,
                button_text: button_text,
            };
        } else {
            return {
                logo: NEWSLETTER_GENERIC.logo,
                slogan: NEWSLETTER_GENERIC.slogan,
                desc: NEWSLETTER_GENERIC.desc,
                name: NEWSLETTER_GENERIC.name,
                is_subscribed: false,
                tried_subscribe: false,
                success_message: NEWSLETTER_GENERIC.success_message,
            };
        }
    };

    const [state, setState] = useState({
        newsletter_config: promotenewsletter,
        ...getInitialState(),
    });

    useEffect(() => {
        let newsletter_config = promotenewsletter;
        if (newsletter_config === "78" || newsletter_config === "81") {
            newsletter_config = "true";
        }

        setState((state) => ({
            ...state,
            newsletter_config: newsletter_config,
        }));
    }, []);

    useEffect(() => {
        const newsletter = promotenewsletter;
        const subscribe_info_exists = subscribe && subscribe[0] !== undefined;
        const is_new_widget =
            newsletter !== previousValuesRef.current.promotenewsletter;
        const previousValues = {
            promotenewsletter,
            name: state.name,
        };
        let subscribe_info = {},
            subscribe_id_matches = false;
        if (subscribe_info_exists) {
            subscribe_info = getSubInfo();
            subscribe_id_matches =
                subscribe_info && typeof subscribe_info === "object";
        }
        if (
            state.newsletter_config &&
            state.newsletter_config !== "true" &&
            newsletters
        ) {
            const config = newsletters.find(
                (n) => n["newsletter_id"] === newsletter
            );
            const sub_success = subscribe_info && subscribe_info.message;
            // set up new widget
            if (
                (config && previousValuesRef.current.name !== config?.name) ||
                is_new_widget
            ) {
                const success_message = getSuccessMessage(
                    subscribe_info,
                    config
                );

                const logo_image = getLogoImage(config);

                const button_text =
                    config?.button_text.trim() !== ""
                        ? config?.button_text
                        : "Subscribe";
                setState((s) => ({
                    ...s,
                    newsletter_config: newsletter,
                    logo: logo_image,
                    slogan: "",
                    desc: config?.description_widget,
                    name: config?.name,
                    is_subscribed: sub_success,
                    tried_subscribe: false,
                    success_message,
                    button_text: button_text,
                }));
                previousValues.name = config?.name;
                // subscription status changed for current widget
            } else if (
                subscribe_id_matches &&
                (!state.tried_subscribe || sub_success !== state.is_subscribed)
            ) {
                const success_message = getSuccessMessage(
                    subscribe_info,
                    config
                );
                setState((s) => ({
                    ...s,
                    tried_subscribe: true,
                    subscribe_info,
                    is_subscribed: sub_success,
                    success_message,
                }));
            }
        }
        previousValuesRef.current = previousValues;
    }, [promotenewsletter, subscribe, state.name, newsletters]);

    const newsletterForm = (
        <NewsletterForm
            newsletter={state.newsletter_config}
            desc={state.desc}
            name={state.name}
            subscribeAction={postSubscribeNewslettersEmail}
            subscribe={subscribe}
            success_message={state.success_message}
            is_subscribed={state.is_subscribed}
            tried_subscribe={state.tried_subscribe}
            subscribe_info={state.subscribe_info}
            button_text={state.button_text}
        />
    );

    const newsletterTitle = state.logo ? (
        <h2 className="newsletterLogoHeading">
            <img
                src={state.logo}
                className="newsletterWidgetLogo"
                alt={state.name}
                height="auto"
                width="260"
                loading="lazy"
            />
        </h2>
    ) : (
        <Heading
            text={state.name}
            level={"h2"}
            levelClassName={"h3"}
            className={"newsletterTitle"}
        />
    );

    const footer =
        newsletters && state.newsletter_config?.length === 6 ? (
            newsletterForm
        ) : (
            <SubscriptionLink />
        );
    const hideDesc = state.is_subscribed ? "hide" : "";

    return (
        <div
            className={`newsletterWidget newsletterWidget-${promotenewsletter}`}
            data-testid="newsletterWidget"
        >
            <div className="newsletterWidgetTextWrapper">
                {newsletterTitle}
                <div className="newsletterWidgetSloganDesc">
                    <p className={`newsletterWidgetSlogan ${hideDesc}`}>
                        {state.slogan}
                    </p>
                    <p className={`newsletterWidgetDesc ${hideDesc}`}>
                        {state.desc}
                    </p>
                </div>
            </div>
            {footer}
        </div>
    );
};

function SubscriptionLink() {
    return (
        <a
            href="https://subscriptions.cbc.ca/listmanagement?cmp=feed_generic_newsletter-subscribe"
            rel="noopener noreferrer"
            className="newsletterWidgetLink"
        >
            Sign up now
        </a>
    );
}

export default NewsletterWidget;
